import { useTranslation } from 'react-i18next';

import CopyButton from '@/components/atoms/CopyButton/CopyButton';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { Label } from '@/components/ui/label';
import { TabsContent } from '@/components/ui/tabs';

import type { EvaluationWithSimulation } from '../../../types';
import { CHANNEL_LABEL_KEYS } from '../../../types';
import { CHANNEL_ICONS, formatDurationSeconds } from '../../../utils';

interface CopyableFieldProps {
  id: string;
  label: string;
  value: string | undefined;
  emptyMessage: string;
}

const CopyableField = ({ id, label, value, emptyMessage }: CopyableFieldProps) => (
  <div className="flex max-w-96 flex-col gap-2">
    <Label htmlFor={id}>{label}</Label>
    {value ? (
      <InputGroup>
        <InputGroupInput id={id} readOnly value={value} className="text-muted-foreground" />
        <InputGroupAddon align="inline-end">
          <CopyButton data={value} iconSize={14} />
        </InputGroupAddon>
      </InputGroup>
    ) : (
      <p className="text-sm text-muted-foreground">{emptyMessage}</p>
    )}
  </div>
);

interface ConversationTabProps {
  evaluation: EvaluationWithSimulation;
  hasTurnSelected: boolean;
  selectedRequestId: string | undefined;
}

export const ConversationTab = ({
  evaluation,
  hasTurnSelected,
  selectedRequestId,
}: ConversationTabProps) => {
  const { t } = useTranslation();
  const { simulation } = evaluation;
  const ChannelIcon = CHANNEL_ICONS[simulation.channel_type];

  return (
    <TabsContent value="conversation" className="flex-1 space-y-6 overflow-y-auto px-4 py-6">
      <CopyableField
        id="simulation-conversation-id"
        label={t('simulations.conversation_id')}
        value={evaluation.session_id ?? undefined}
        emptyMessage={t('common.not_available')}
      />

      <CopyableField
        id="simulation-request-id"
        label={t('analytics.fields.request_id')}
        value={selectedRequestId}
        emptyMessage={
          hasTurnSelected
            ? t('simulations.request_id_unavailable')
            : t('simulations.select_turn_to_see_request_id')
        }
      />

      <div className="flex flex-col gap-0.5">
        <span className="text-xs leading-4 text-muted-foreground">{t('simulations.channel')}</span>
        <div className="flex items-center gap-2">
          <ChannelIcon size={16} className="text-muted-foreground" />
          <span className="text-sm leading-5 text-foreground">
            {t(CHANNEL_LABEL_KEYS[simulation.channel_type])}
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-0.5">
        <span className="text-xs leading-4 text-muted-foreground">{t('simulations.turns')}</span>
        <p className="text-sm leading-5 text-foreground">{evaluation.turn_count ?? '-'}</p>
      </div>

      <div className="flex flex-col gap-0.5">
        <span className="text-xs leading-4 text-muted-foreground">{t('simulations.duration')}</span>
        <p className="text-sm leading-5 text-foreground">
          {formatDurationSeconds(evaluation.duration_seconds)}
        </p>
      </div>
    </TabsContent>
  );
};
